/**
 * CLI <-> Canonical evaluation criterion adapter.
 *
 * The BE stores criteria in CANONICAL shape (aligned with metatuner):
 *   { id?, criteria, evaluationParameter, evaluationSteps?, name? }
 *
 * The CLI exposes criteria in CLI shape (user-facing field names):
 *   { name?, description, evaluationParameter }
 *
 * Adapters live at the CLI boundary so command UX stays unchanged while the
 * BE contract is enforced. The BE is strict on both read and write (post-backfill
 * #352) — no legacy fallback chains needed.
 */

export interface CliCriterion {
  name?: string;
  description: string;
  evaluationParameter: string;
}

export interface CanonicalCriterion {
  id?: string;
  criteria: string;
  evaluationParameter: string;
  evaluationSteps?: string[];
  name?: string;
}

/** Convert a CLI-shaped criterion to canonical wire/storage shape. */
export function cliCriterionToCanonical(c: CliCriterion): CanonicalCriterion {
  return {
    criteria: c.description,
    evaluationParameter: c.evaluationParameter,
    name: c.name,
  };
}

/** Convert a canonical criterion to CLI display shape. */
export function canonicalCriterionToCli(c: CanonicalCriterion): CliCriterion {
  return {
    name: c.name ?? c.id,
    description: c.criteria,
    evaluationParameter: c.evaluationParameter,
  };
}

export function cliCriteriaArrayToCanonical(arr: CliCriterion[]): CanonicalCriterion[] {
  if (!Array.isArray(arr)) return [];
  return arr.map(cliCriterionToCanonical);
}

export function canonicalCriteriaArrayToCli(arr: CanonicalCriterion[] | undefined | null): CliCriterion[] {
  if (!Array.isArray(arr)) return [];
  return arr.map(canonicalCriterionToCli);
}
